// src/composables/useProfileDocument.js
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import analytics from '@/@core/utils/analytics'
import constants, { FILE_TYPES } from '@/constants'
import { UPDATE_ENTITY } from '@/store/modules/auth.module'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import { apiToastSuccess } from '@/@core/utils/toast'
import { convertDateStringToDateObj } from '@/common-utils'

export function useProfileDocument() {
  // --- Reactive State ---
  const loading = ref(false)
  const documentLocal = ref({})
  const companyName = ref(null)
  const tradeLicenseFile = ref(null)
  const tradeLicenseNumber = ref(null)
  const vatCertificateFile = ref(null)
  const vatCertificateNumber = ref(null)
  const companyProfileFile = ref(null)
  const othersFile = ref(null)
  const tradeLicenseExpiryDate = ref('')
  const dateOfIncorporation = ref('')

  // --- Computed ---
  const isRetailer = computed(() => store.getters.isRetailer)
  const isNotEntityAdmin = computed(() => !store.getters.isEntityAdmin)

  // --- Methods ---
  function buildDefault() {
    return {
      tradeLicense: '',
      vatCertificate: '',
      companyProfile: '',
      others: '',
      tradeLicenseNumber: '',
      vatCertificateNumber: '',
      tradeLicenseExpiryDate: '',
      dateOfIncorporation: '',
      companyName: '',
    }
  }

  function initDocument(documentData) {
    const data = documentData || buildDefault()
    documentLocal.value = JSON.parse(JSON.stringify(data))
    companyName.value = data.companyName || null
    tradeLicenseNumber.value = data.tradeLicenseNumber || null
    vatCertificateNumber.value = data.vatCertificateNumber || null
    tradeLicenseExpiryDate.value = data.tradeLicenseExpiryDate || ''
    dateOfIncorporation.value = data.dateOfIncorporation || ''
  }

  async function uploadFile(fileRef, fileType) {
    if (fileRef.value instanceof File) {
      try {
        const res = await store.dispatch(UPLOAD_DOCUMENTS, { fileType, file: fileRef.value })
        return res
      } catch {
        return null
      }
    }
    return null
  }

  const fileUploadEntries = [
    { fileRef: tradeLicenseFile, type: FILE_TYPES?.TRADE_LICENSE || 'tradeLicense', key: 'tradeLicense' },
    { fileRef: vatCertificateFile, type: FILE_TYPES?.VAT_CERTIFICATE || 'vatCertificate', key: 'vatCertificate' },
    { fileRef: companyProfileFile, type: FILE_TYPES?.COMPANY_PROFILE || 'companyProfile', key: 'companyProfile' },
    { fileRef: othersFile, type: FILE_TYPES?.OTHERS || 'others', key: 'others' },
  ]

  async function uploadAllFiles() {
    const results = await Promise.all(
      fileUploadEntries.map(entry => uploadFile(entry.fileRef, entry.type)),
    )
    results.forEach((res, i) => {
      if (res) documentLocal.value[fileUploadEntries[i].key] = res
    })
  }

  function anyUploadFailed(docsBefore) {
    return fileUploadEntries.some(
      entry => entry.fileRef.value instanceof File && documentLocal.value[entry.key] === docsBefore[entry.key],
    )
  }

  function prepareDocumentData() {
    documentLocal.value.companyName = companyName.value
    documentLocal.value.tradeLicenseNumber = tradeLicenseNumber.value
    documentLocal.value.vatCertificateNumber = vatCertificateNumber.value
    if (tradeLicenseExpiryDate.value) {
      documentLocal.value.tradeLicenseExpiryDate = convertDateStringToDateObj(tradeLicenseExpiryDate.value)
    }
    if (dateOfIncorporation.value) {
      documentLocal.value.dateOfIncorporation = convertDateStringToDateObj(dateOfIncorporation.value)
    }
  }

  function buildCleanPayload() {
    const payload = { ...documentLocal.value }
    Object.keys(payload).forEach(key => {
      if (payload[key] === '' || payload[key] === null) delete payload[key]
    })
    return payload
  }

  async function uploadDocuments() {
    loading.value = true
    try {
      prepareDocumentData()
      const docsBefore = { ...documentLocal.value }

      await uploadAllFiles()

      if (anyUploadFailed(docsBefore)) return { success: false }

      await store.dispatch(UPDATE_ENTITY, {
        ...store.getters.entityData,
        ...buildCleanPayload(),
      })
      apiToastSuccess('Documents updated successfully')
      analytics.track(constants.TRACKS.ACTIONS.SAVE_PROFILE_SETTINGS_DOCUMENT)
      return { success: true, payload: documentLocal.value }
    } catch {
      return { success: false }
    } finally {
      loading.value = false
    }
  }

  function resetForm(documentData) {
    initDocument(documentData)
    tradeLicenseFile.value = null
    vatCertificateFile.value = null
    companyProfileFile.value = null
    othersFile.value = null
  }

  return {
    loading,
    documentLocal,
    companyName,
    tradeLicenseFile,
    tradeLicenseNumber,
    vatCertificateFile,
    vatCertificateNumber,
    companyProfileFile,
    othersFile,
    tradeLicenseExpiryDate,
    dateOfIncorporation,
    isRetailer,
    isNotEntityAdmin,
    initDocument,
    uploadDocuments,
    resetForm,
  }
}
